import { useMemo, useState } from 'react';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import { StyledButton } from 'components/Buttons';
import { useAuth } from 'contexts/authContext';
import { FetchInvoiceData } from 'pages/invoices/components/InvoiceDetails/InvoiceDetails.types';
import {
  CorrectiveInvoice,
  Invoice,
  InvoiceLine,
} from 'pages/invoices/invoices.types';
import { useTranslation } from 'react-i18next';
import {
  canEditInvoice,
  isInvoiceCorrective,
} from 'utils/invoiceFunctions.utils';
import {
  formatNetUnitPrice,
  toFixedCurrency,
} from 'utils/numberFunctions.utils';
import * as D from '../../InvoiceLinesPanel.css';
import { InvoiceLineForm } from '../InvoiceLineForm';
import { DetailsField } from './DetailsField';
import * as S from './InvoiceLineDetailsDialog.css';

type Props = {
  open: boolean;
  invoice: Invoice | CorrectiveInvoice;
  invoiceLine: InvoiceLine | null;
  isPrevious?: boolean;
  onClose: () => void;
  refetchInvoiceData: FetchInvoiceData;
};

export const InvoiceLineDetailsDialog = ({
  open,
  onClose,
  invoice,
  invoiceLine,
  isPrevious,
  refetchInvoiceData,
}: Props) => {
  const { user, allowedReplacementsByCode } = useAuth();
  const { t } = useTranslation([
    'misc',
    'invoice',
    'counterparty',
    'currency',
    'clause',
  ]);

  const [isEditing, setisEditing] = useState(false);

  const invoiceLineEntries = useMemo(
    () =>
      invoiceLine
        ? [
            {
              label: t('activity', { ns: 'invoice' }),
              data: invoiceLine.activity.code || '',
              visible: true,
            },
            {
              label: t('originalExternalName', { ns: 'invoice' }),
              data: invoiceLine.originalExternalName || '',
              visible: invoiceLine.activity.code === 'NIEROZPOZNANE',
            },
            {
              label: t('description', { ns: 'misc' }),
              data: invoiceLine.description || '',
              visible: !!invoiceLine.description?.length,
            },
            {
              label: t('amount', { ns: 'misc' }),
              data: invoiceLine.quantity || '',
              visible: !!invoiceLine.quantity,
            },
            {
              label: t('unit', { ns: 'misc' }),
              data: invoiceLine.unit.code || '',
              visible: true,
            },
            {
              label: t('price', { ns: 'invoice' }),
              data: formatNetUnitPrice(invoiceLine.currencyAmount.value),
              visible: true,
            },
            {
              label: t('currency', { ns: 'currency' }),
              data: invoiceLine.currencyAmount.currencyCode.currency || '',
              visible: true,
            },
            {
              label: t('exchangeRate', { ns: 'invoice' }),
              data: toFixedCurrency(invoiceLine.exchangeRate, 4) || '',
              visible: true,
            },
            {
              label: t('netValue', { ns: 'invoice' }),
              data: toFixedCurrency(invoiceLine.amounts.netValue, 2) || '',
              visible: !!invoiceLine.amounts.netValue,
            },
            {
              label: t('taxRate', { ns: 'invoice' }),
              data: invoiceLine.amounts.taxRate.code || '',
              visible: true,
            },
            {
              label: t('taxValue', { ns: 'invoice' }),
              data: toFixedCurrency(invoiceLine.amounts.taxValue, 2) || '',
              visible: !!invoiceLine.amounts.taxValue,
            },
            {
              label: t('grossValue', { ns: 'invoice' }),
              data: toFixedCurrency(invoiceLine.amounts.grossValue, 2) || '',
              visible: !!invoiceLine.amounts.grossValue,
            },
            {
              label: t('vehicleMileage', { ns: 'invoice' }),
              data: invoiceLine.vehicleMileage || '',
              visible: !!invoiceLine.vehicleMileage,
            },
          ]
        : [],
    [invoiceLine?.id],
  );

  const handleClose = () => {
    onClose();
    setisEditing(false);
  };

  const displayEditButton = useMemo(() => {
    if (isInvoiceCorrective(invoice.invoiceClass)) {
      if (isPrevious) {
        return (
          !(invoice as CorrectiveInvoice).generated &&
          canEditInvoice(invoice, user, allowedReplacementsByCode)
        );
      }
      return canEditInvoice(invoice, user, allowedReplacementsByCode);
    }
    return (
      !invoice.publicationDate &&
      !invoice.deleted &&
      canEditInvoice(invoice, user, allowedReplacementsByCode)
    );
  }, [invoiceLine]);

  return (
    <D.StyledDialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      {invoiceLine && (
        <>
          <DialogTitle>
            {`${t('details', { ns: 'misc' })} - ${t('invoice', {
              ns: 'invoice',
            })} ${invoice.code}`}
          </DialogTitle>
          <Divider />
          {!isEditing ? (
            <>
              <DialogContent>
                <S.DetailsContainer>
                  {invoiceLineEntries.map(
                    (entry) =>
                      entry.visible && (
                        <DetailsField
                          key={entry.label}
                          label={entry.label}
                          data={entry.data}
                        />
                      ),
                  )}
                </S.DetailsContainer>
              </DialogContent>
              <Divider />
              <DialogActions>
                {!invoice.publicationDate &&
                  !invoice.deleted &&
                  displayEditButton && (
                    <StyledButton
                      $primary
                      onClick={() => setisEditing((prev) => !prev)}
                    >
                      {t('button.edit', { ns: 'misc' })}
                    </StyledButton>
                  )}
                <StyledButton onClick={handleClose}>
                  {t('button.cancel', { ns: 'misc' })}
                </StyledButton>
              </DialogActions>
            </>
          ) : (
            <InvoiceLineForm
              invoice={invoice}
              refetchInvoiceData={refetchInvoiceData}
              onClose={handleClose}
              invoiceLineData={invoiceLine}
              isEditing={true}
              isPrevious={isPrevious}
            />
          )}
        </>
      )}
    </D.StyledDialog>
  );
};
